import { useRef, useState } from "react";
import { playerIdFromMatchPerson, useOpenPlayerProfile } from "./useOpenPlayerProfile";
import type { IMatchDetails, InningScorecard, IPlayersTeamSquad } from "./types/matchDetails.d";
import type { IVideoHighlight } from "./VideoHighlights";
import { getMatchEquationText, getSeasonYear } from "../../../../../../helper/helper";
import { getMatchDaysText, getMatchHoursOfPlay, getMatchOfficialDisplayName, getMatchPersonDisplayName, getMatchVenueText, getMatchStatusDisplayLabel } from "./matchDetailDisplay";

function formatInningOvers(over: number | undefined | null, ball: number | undefined | null): string {
  if (over == null && ball == null) return "-";
  return `${Number(over ?? 0)}.${Number(ball ?? 0)}`;
}

function formatInningScore(runs: number | undefined | null, wicket: number | undefined | null): string {
  if (runs == null && wicket == null) return "-";
  return `${runs ?? 0}/${wicket ?? 0}`;
}

interface ScoreCardProps {
  matchSummary: IMatchDetails | undefined;
  scores: InningScorecard[] | undefined;
  videos?: IVideoHighlight[]; // Optional videos array
  teamOnePlayers?: IPlayersTeamSquad;
  teamTwoPlayers?: IPlayersTeamSquad;
}

const MatchesScoreCard = ({ matchSummary, scores, teamOnePlayers, teamTwoPlayers }: ScoreCardProps) => {
  const openPlayerProfile = useOpenPlayerProfile();
  const venueText = getMatchVenueText(matchSummary);

  const renderRoleBadges = (playerRole: string | undefined | null) => {
    if (!playerRole) return null;
    const role = playerRole.toUpperCase();
    if (role === "CAPTAIN") return <span className="player-role-badge badge-captain">C</span>;
    if (role === "WICKET_KEEPER") return <span className="player-role-badge badge-wk">WK</span>;
    if (role === "CAPTAIN_WICKET_KEEPER")
      return (
        <>
          <span className="player-role-badge badge-captain">C</span>
          <span className="player-role-badge badge-wk">WK</span>
        </>
      );
    return null;
  };

  const renderLinkedPlayerNameTd = (playerId: string | undefined, firstName?: string | null, lastName?: string | null, playerRole?: string | null) => {
    const label = [firstName, lastName].filter((x) => x?.trim()).join(" ") || "-";
    const id = playerId?.trim();
    const badges = renderRoleBadges(playerRole);
    if (!id) {
      return (
        <td className="player-name">
          {label}
          {badges}
        </td>
      );
    }
    return (
      <td className="player-name">
        <button type="button" className="player-name-link" onClick={() => openPlayerProfile(id)}>
          {label}
        </button>
        {badges}
      </td>
    );
  };

  const squadOne = teamOnePlayers?.playingSquad ?? [];
  const squadTwo = teamTwoPlayers?.playingSquad ?? [];

  // ── Resolve which squad is batting in a given inning ──────────────────────
  // We can't rely on position (scores[0] = teamOne) because whichever team wins
  // the toss bats first. Instead we count player-ID overlaps between the inning's
  // batting list and each squad, and pick the better-matching one.
  // Fallback: compare battingTeamName to the match summary team names.
  const resolveSquadForInning = (inning: InningScorecard | undefined) => {
    if (!inning) return undefined;

    const batterIds = new Set<string>([...(inning.battingScoreCardResponseList?.map((b) => b.playerId) ?? []), inning.striker?.playerId, inning.nonStriker?.playerId].filter((id): id is string => !!id));

    if (batterIds.size > 0) {
      const oneMatches = squadOne.filter((p) => batterIds.has(p.playerId)).length;
      const twoMatches = squadTwo.filter((p) => batterIds.has(p.playerId)).length;
      // If one squad clearly matches more batters, use it
      if (oneMatches !== twoMatches) {
        return oneMatches > twoMatches ? squadOne : squadTwo;
      }
    }

    // Fallback: name comparison (covers innings not yet started)
    const battingName = inning.battingTeamName?.trim().toLowerCase();
    const nameOne = matchSummary?.teamOne?.teamName?.trim().toLowerCase();
    return battingName && nameOne && battingName === nameOne ? squadOne : squadTwo;
  };

  // ── Helper: compute "yet to bat / did not bat" players for a given inning ──
  const getUnsettledBatters = (inning: InningScorecard | undefined) => {
    const squad = resolveSquadForInning(inning);
    if (!squad?.length || !inning) return [];
    const isAllOut = (inning.wicket ?? 0) >= 10;
    if (isAllOut) return [];

    // Build set of player IDs who have already batted or are currently at crease
    const activeBattedIds = new Set<string>([...(inning.battingScoreCardResponseList?.map((b) => b.playerId) ?? []), inning.striker?.playerId, inning.nonStriker?.playerId].filter((id): id is string => !!id));

    return squad.filter((p) => p.playerId && !activeBattedIds.has(p.playerId));
  };

  const isInningLive = (inning: InningScorecard | undefined) => inning?.inningStatus === "IN_PROGRESS" || inning?.inningStatus === "STARTED";

  // ── Tab label for an inning ───────────────────────────────────────────────
  // Prefer the batting team's short code; fall back to the batting team name.
  // When a team bats more than once (multi-innings formats) the order down the
  // page disambiguates, but we also append an occurrence suffix so the tabs
  // don't read as duplicates.
  const getInningTeamCode = (inning: InningScorecard | undefined) => {
    const name = inning?.battingTeamName?.trim().toLowerCase();
    if (name) {
      if (matchSummary?.teamOne?.teamName?.trim().toLowerCase() === name) return matchSummary.teamOne.teamCode || matchSummary.teamOne.teamName;
      if (matchSummary?.teamTwo?.teamName?.trim().toLowerCase() === name) return matchSummary.teamTwo.teamCode || matchSummary.teamTwo.teamName;
    }
    return inning?.battingTeamName ?? "-";
  };

  const innings = scores ?? [];

  // Count how many times each code appears so we can add a " 2"/" 3" suffix
  // only when a team genuinely bats multiple times.
  const codeTotals = innings.reduce<Record<string, number>>((acc, inning) => {
    const code = getInningTeamCode(inning);
    acc[code] = (acc[code] ?? 0) + 1;
    return acc;
  }, {});

  const seenCounts: Record<string, number> = {};
  const tabLabels = innings.map((inning) => {
    const code = getInningTeamCode(inning);
    seenCounts[code] = (seenCounts[code] ?? 0) + 1;
    return codeTotals[code] > 1 ? `${code} ${seenCounts[code]}` : code;
  });

  const [activeTab, setActiveTab] = useState<number>(0);
  // One ref per inning, plus a dedicated ref for the Match Details section.
  const inningRefs = useRef<(HTMLDivElement | null)[]>([]);
  const matchDetailsRef = useRef<HTMLDivElement>(null);

  // Match Details tab sits after all the inning tabs.
  const matchDetailsTabIndex = innings.length;

  const handleTabClick = (tabIndex: number) => {
    setActiveTab(tabIndex);

    const targetElement: HTMLElement | null = tabIndex === matchDetailsTabIndex ? matchDetailsRef.current : inningRefs.current[tabIndex] ?? null;

    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const renderInning = (inning: InningScorecard, index: number) => {
    const unsettled = getUnsettledBatters(inning);
    const nb = inning.noBall ?? 0;
    const w = inning.wide ?? 0;
    const b = inning.bye ?? 0;
    const lb = inning.legBye ?? 0;
    const pen = inning.penalty ?? 0;
    const extrasTotal = nb + w + b + lb + pen;
    const fallOfWickets = inning.fallOfWickets as unknown as (string | number)[] | undefined;

    return (
      <div
        className={`team-wrap ${index > 0 ? "second-team" : ""}`}
        ref={(el) => {
          inningRefs.current[index] = el;
        }}
        key={inning.inningId ?? inning.id ?? index}
      >
        <div className="team-wrapper">
          <p className="nav-right-text">
            <span className="text-wrapper">{inning.battingTeamName ?? "-"} </span>
            <span className="over-span">({inning.maxOvers ?? "-"} Ovs Maximum)</span>
          </p>
          <p className="nav-left-text">
            <span className="text-wrapper-2">Overs ({formatInningOvers(inning.over, inning.ball)})</span>
            <span className="text-wrapper-3">&nbsp;</span>
            <span className="text-wrapper-4"> {formatInningScore(inning.runs, inning.wicket)}</span>
          </p>
        </div>
        <div className="table-wrapper">
          <table className="stats-table">
            <thead>
              <tr className="header-row">
                <th>Batting</th>
                <th></th>
                <th>R</th>
                <th>B</th>
                <th>4s</th>
                <th>6s</th>
                <th>SR</th>
              </tr>
            </thead>
            <tbody>
              {inning.battingScoreCardResponseList?.map((item) => (
                <tr key={item.id}>
                  {renderLinkedPlayerNameTd(item?.playerId, item?.playerFirstName, item?.playerLastName, item?.playerRole)}
                  <td>{item?.outString ?? "-"}</td>
                  <td className="stats">{item?.run ?? "-"}</td>
                  <td className="stats">{item?.balls ?? "-"}</td>
                  <td className="stats">{item?.fours ?? "-"}</td>
                  <td className="stats">{item?.six ?? "-"}</td>
                  <td className="stats">{item?.strikeRate != null ? Number(item.strikeRate).toFixed(2) : "-"}</td>
                </tr>
              ))}
              <tr className="extras-row">
                <td className="player-name">Extras</td>
                <td>{`( nb ${nb}, w ${w}, b ${b}, lb ${lb}, pen ${pen} )`}</td>
                <td className="stats">{extrasTotal}</td>
                <td colSpan={4}></td>
              </tr>
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td>Total</td>
                <td>
                  {formatInningOvers(inning.over, inning.ball)} Ov (CRR: {inning.currentRunrate != null ? Number(inning.currentRunrate).toFixed(2) : "-"})
                </td>
                <td colSpan={5}>{inning.runs ?? "-"}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="fall-wickets">
          <p className="nav-text">
            <span className="text-wrapper">Fall Of Wickets:</span>
            <span className="span"> </span>
            <span className="span">{fallOfWickets && fallOfWickets.length > 0 ? fallOfWickets.join(", ") : "-"}</span>
          </p>
          {unsettled.length > 0 && (
            <div className="did-not-bat">
              <p className="nav-text">
                <span className="text-wrapper">{isInningLive(inning) ? "Yet to Bat:" : "Did Not Bat:"}</span>
                <span className="span">
                  {" "}
                  {unsettled.map((p, i) => (
                    <span key={p.playerId || `inning-${index}-ytb-${i}`}>
                      {i > 0 ? ", " : ""}
                      {p.playerId?.trim() ? (
                        <button type="button" className="player-name-link" onClick={() => openPlayerProfile(p.playerId)}>
                          {`${p.playerFirstName ?? ""} ${p.playerLastName ?? ""}`.trim()}
                        </button>
                      ) : (
                        `${p.playerFirstName ?? ""} ${p.playerLastName ?? ""}`.trim()
                      )}
                    </span>
                  ))}
                </span>
              </p>
            </div>
          )}
        </div>
        <div className="table-wrapper">
          <table className="stats-table bowling-stats-table">
            <thead>
              <tr className="header-row">
                <th>Bowling</th>
                <th>O</th>
                <th>M</th>
                <th>R</th>
                <th>W</th>
                <th>Econ.</th>
                <th>Dots</th>
                <th>WD</th>
                <th>NB</th>
              </tr>
            </thead>
            <tbody>
              {inning.bowlingScoreCardResponses?.map((item) => (
                <tr key={item.id}>
                  {renderLinkedPlayerNameTd(item?.playerId, item?.playerFirstName, item?.playerLastName, item?.playerRole)}
                  <td className="stats">{item?.over != null ? `${item.over}.${item?.ball ?? 0}` : "-"}</td>
                  <td className="stats">{item?.maiden ?? "-"}</td>
                  <td className="stats">{item?.runs ?? "-"}</td>
                  <td className="stats">{item?.wickets ?? "-"}</td>
                  <td className="stats">{item?.economy != null ? Number(item.economy).toFixed(2) : "-"}</td>
                  <td className="stats">{item?.dotBall ?? "-"}</td>
                  <td className="stats">{item?.wide ?? "-"}</td>
                  <td className="stats">{item?.noBall ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (!scores?.length) {
    return null;
  }
  return (
    <>
      <div className="matches-scorecard-wrapper">
        <div className="scorecard-content-wrapper">
          <div className="scorecard-main">
            <div className="scorecard-tabs">
              {tabLabels.map((label, index) => (
                <div key={`inning-tab-${index}`} className={`tab-item ${activeTab === index ? "active" : ""}`} onClick={() => handleTabClick(index)}>
                  <p>{label}</p>
                </div>
              ))}
              <div className={`tab-item ${activeTab === matchDetailsTabIndex ? "active" : ""}`} onClick={() => handleTabClick(matchDetailsTabIndex)}>
                <p>Match Details</p>
              </div>
            </div>
            {innings.map((inning, index) => renderInning(inning, index))}
            <div className="match-details-wrap" ref={matchDetailsRef}>
              <div className="team-wrapper">
                <p className="nav-right-text">
                  <span className="text-wrapper">Match Details</span>
                </p>
              </div>
              <div className="location">
                <p>{venueText}</p>
              </div>
              <div className="match-details-table-wrap">
                <table>
                  <tbody>
                    <tr>
                      <th scope="row">Toss</th>
                      <td>{matchSummary?.tossResult}</td>
                    </tr>

                    <tr className="series-row">
                      <th scope="row">Series</th>
                      <td>{matchSummary?.tournament?.tournamentName}</td>
                    </tr>

                    <tr>
                      <th scope="row">Season</th>
                      <td className="is-strong">{getSeasonYear(matchSummary?.startDate)}</td>
                    </tr>

                    <tr>
                      <th scope="row">Player of The Match</th>
                      <td>
                        {(() => {
                          const potm = matchSummary?.playerOfTheMatch ?? null;
                          const pid = playerIdFromMatchPerson(potm);
                          const name = getMatchPersonDisplayName(potm);
                          if (!pid) return name;
                          return (
                            <button type="button" className="player-name-link" onClick={() => openPlayerProfile(pid)}>
                              {name}
                            </button>
                          );
                        })()}
                      </td>
                    </tr>

                    <tr>
                      <th scope="row">League</th>
                      <td>{matchSummary?.leagueName ?? "-"}</td>
                    </tr>

                    <tr>
                      <th scope="row">Hours of Play (local time)</th>
                      <td>{getMatchHoursOfPlay(matchSummary)}</td>
                    </tr>

                    <tr>
                      <th scope="row">Match days</th>
                      <td>{getMatchDaysText(matchSummary)}</td>
                    </tr>

                    <tr>
                      <th scope="row">Umpire</th>
                      <td>{getMatchOfficialDisplayName(matchSummary?.umpire ?? matchSummary?.availableUmpireResponse)}</td>
                    </tr>

                    <tr>
                      <th scope="row">Second Umpire</th>
                      <td>{getMatchOfficialDisplayName(matchSummary?.umpireTwo)}</td>
                    </tr>

                    <tr>
                      <th scope="row">Third Umpire</th>
                      <td>{getMatchOfficialDisplayName(matchSummary?.thirdUmpire)}</td>
                    </tr>

                    <tr>
                      <th scope="row">Match Status</th>
                      <td>{getMatchStatusDisplayLabel(matchSummary?.matchStatus)}</td>
                    </tr>

                    <tr>
                      <th scope="row">Result</th>
                      <td>{getMatchEquationText(matchSummary ? { ...matchSummary, inningScorecardList: scores ?? matchSummary.inningScorecardList } : matchSummary)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MatchesScoreCard;
